import { Route, Switch, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DeleteEventMutation from './DeleteEventMutation';
import EditEventBrandingForm from './EditEventBrandingForm';
import EditEventDashboardSettingsForm from './EditEventDashboardSettingsForm';
import EditEventInfoForm from './EditEventInfoForm';
import EditEventInterestListForm from './EditEventInterestListForm';
import EditEventLocaleForm from './EditEventLocaleForm';
import EditEventPaymentSettingsForm from './EditEventPaymentSettingsForm';
import EditEventPreregistrationForm from './EditEventPreregistrationForm';
import EditEventQueueForm from './EditEventQueueForm';
import EditEventRegistrationForm from './EditEventRegistrationForm';
import EditEventRestrictionsForm from './EditEventRestrictionsForm';
import EditEventTermsForm from './EditEventTermsForm';
import EditEventTicketSettingsForm from './EditEventTicketSettingsForm';
import EditEventWaitlistForm from './EditEventWaitlistForm';
import GetEventSettingsQuery from './GetEventSettingsQuery';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

export interface SettingsPageProps {
  eventId: string;
}

const SettingsPage = ({ eventId }: SettingsPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const notifier = useNotifier();
  const project = useProject();

  const { data } = useQuery(GetEventSettingsQuery, {
    variables: {
      project_id: project.id,
      event_id: eventId,
    },
    fetchPolicy: 'network-only',
  });

  const event = data?.project.event;

  const [deleteEvent, { loading }] = useMutation(DeleteEventMutation, {
    variables: {
      id: eventId,
    },
    onCompleted: () => {
      notifier.success(t('notifications.event_deleted'));

      history.push(`/${project.id}/events`);
    },
  });

  const mailPermissions = data?.project.mail_permissions || [];
  const paymentProfiles = data?.project.payment_profiles || [];

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.SectionHeading>
          {t('event_settings')}
        </UI.SectionHeading>

        <UI.Card sc={{ spacing: 'xl' }}>
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
            <UI.Nav>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings`}
                exact
                resetScrollPosition={false}
              >
                {t('event_info')}
                <UI.Label sc={{ brand: 'secondary', small: true, ml: 1, fontWeight: 500 }}>
                  {t('new')}
                </UI.Label>
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/branding`}
                resetScrollPosition={false}
              >
                {t('branding')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/registration`}
                resetScrollPosition={false}
              >
                {t('registration')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/leads`}
                resetScrollPosition={false}
              >
                {t('lead_generation')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/waitlist`}
                resetScrollPosition={false}
              >
                {t('waitlist')}
              </UI.NavLink>
              {event.has_preregistrations && (
                <UI.NavLink
                  to={`/${project.id}/events/${event.id}/settings/preregistration`}
                  resetScrollPosition={false}
                >
                  {t('preregistration')}
                </UI.NavLink>
              )}
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/restrictions`}
                resetScrollPosition={false}
              >
                {t('restrictions')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/dashboard`}
                resetScrollPosition={false}
              >
                {t('participant_dashboard')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/tickets`}
                resetScrollPosition={false}
              >
                {t('ticket_pdfs')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/payments`}
                resetScrollPosition={false}
              >
                {t('payments')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/terms`}
                resetScrollPosition={false}
              >
                {t('terms_and_conditions')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/queue`}
                resetScrollPosition={false}
              >
                {t('queue')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/locale`}
                resetScrollPosition={false}
              >
                {t('locale')}
              </UI.NavLink>
              {event.deletable && (
                <UI.ConfirmButton
                  onConfirm={() => deleteEvent()}
                  sc={{ color: 'error' }}
                  loading={loading}
                  render={(start, disabled) => (
                    <UI.Button
                      onClick={() => start()}
                      sc={{ color: 'error' }}
                      disabled={disabled}
                    >
                      {t('delete')}
                    </UI.Button>
                  )}
                />
              )}
            </UI.Nav>
            <UI.Div>
              <Switch>
                <Route path="/:projectId/events/:eventId/settings/restrictions">
                  <EditEventRestrictionsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/locale">
                  <EditEventLocaleForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/registration">
                  <EditEventRegistrationForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/leads">
                  <EditEventInterestListForm event={event} mailPermissions={mailPermissions} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/waitlist">
                  <EditEventWaitlistForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/preregistration">
                  <EditEventPreregistrationForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/dashboard">
                  <EditEventDashboardSettingsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/branding">
                  <EditEventBrandingForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/tickets">
                  <EditEventTicketSettingsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/payments">
                  <EditEventPaymentSettingsForm event={event} paymentProfiles={paymentProfiles} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/terms">
                  <EditEventTermsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/queue">
                  <EditEventQueueForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings">
                  <EditEventInfoForm event={event} />
                </Route>
              </Switch>
            </UI.Div>
          </UI.FormGrid>
        </UI.Card>
      </UI.GridContainer>
    </UI.Section>
  );
};

export default SettingsPage;
